// API service for communicating with the backend
import { isTokenExpiredOrExpiring } from '../utils/tokenExpiry';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class ApiError extends Error {
    constructor(message, status, data) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }
}

const handleResponse = async (response) => {
    // Some errors (notably nginx's 413 on oversized uploads) return HTML, not JSON.
    // Parse defensively so we surface a clear message instead of a JSON parse crash.
    let data = {};
    try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
    } catch (parseError) {
        data = {};
    }

    if (!response.ok) {
        let message = data.error;
        if (!message) {
            if (response.status === 413) {
                message = 'Fichier trop volumineux. La taille totale de l\'envoi dépasse la limite autorisée (1 Go).';
            } else {
                message = 'Une erreur est survenue';
            }
        }
        throw new ApiError(message, response.status, data);
    }

    return data;
};

const isAuthEndpoint = (endpoint) =>
    endpoint.includes('/auth/login') ||
    endpoint.includes('/auth/register') ||
    endpoint.includes('/auth/refresh') ||
    endpoint.includes('/auth/change-password');

// Refresh the access token using the httpOnly refresh cookie.
// Concurrent callers share a single in-flight refresh (the refresh token is not
// rotated server-side, so this is safe and avoids a burst of refresh calls).
let refreshPromise = null;
const refreshAccessToken = async () => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
            throw new ApiError('Session expirée. Veuillez vous reconnecter.', 401);
        }
        const data = await response.json();
        if (!data.success || !data.data || !data.data.accessToken) {
            throw new ApiError('Session expirée. Veuillez vous reconnecter.', 401);
        }
        localStorage.setItem('accessToken', data.data.accessToken);
        return data.data.accessToken;
    })();

    try {
        return await refreshPromise;
    } finally {
        refreshPromise = null;
    }
};

const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const authEndpoint = isAuthEndpoint(endpoint);

    let token = localStorage.getItem('accessToken');

    // Proactive refresh: if the access token is about to expire, refresh BEFORE
    // sending the request. This is essential for large uploads — otherwise the
    // first attempt 401s, the client re-sends the whole file, and the upstream
    // resets the connection (surfaced as a 502 by the reverse proxy).
    //
    // For file uploads we ALWAYS refresh first, regardless of the local expiry
    // check: auth runs before multer reads the body, so an early 401 arrives
    // while the browser is still uploading and the connection is reset — a
    // network error the reactive retry below can't catch. A skewed client clock
    // (common on office PCs) makes isTokenExpiredOrExpiring miss this, so we
    // don't rely on it here. A freshly minted token is valid on the server clock
    // and only needs to be valid when the headers arrive.
    const isFileUpload = options._isFileUpload;
    if (token && !authEndpoint && (isFileUpload || isTokenExpiredOrExpiring(token, Date.now()))) {
        try {
            token = await refreshAccessToken();
        } catch (refreshError) {
            // Proceed with the current token; the reactive path below will handle it.
        }
    }

    // `headers` est construit APRÈS le spread de `options` : sinon un appelant qui
    // passe `headers` (même un objet vide) écrase tout le bloc, Authorization inclus
    // — c'est ce qui faisait 401 tous les uploads, puis les renvoyait en entier.
    const config = {
        credentials: 'include', // Important pour les cookies
        ...options,
        headers: {
            // Only set Content-Type for JSON if body is not FormData
            ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
            // Ajouter le token d'authentification si disponible
            ...(token && { 'Authorization': `Bearer ${token}` }),
            ...options.headers,
        },
    };

    try {
        const response = await fetch(url, config);
        return await handleResponse(response);
    } catch (error) {
        // Reactive fallback: a 401 slipped through (e.g. token revoked). Refresh once and retry.
        if (error instanceof ApiError && error.status === 401 && !authEndpoint && token) {
            try {
                const newToken = await refreshAccessToken();

                const retryConfig = {
                    ...config,
                    headers: { ...config.headers, Authorization: `Bearer ${newToken}` },
                };
                // Si c'est un upload de fichier, recréer le FormData
                if (config._isFileUpload && config._formDataCreator) {
                    retryConfig.body = config._formDataCreator();
                }

                const retryResponse = await fetch(url, retryConfig);
                return await handleResponse(retryResponse);
            } catch (refreshError) {
                localStorage.removeItem('accessToken');
                if (refreshError instanceof ApiError) throw refreshError;
                throw new ApiError('Session expirée. Veuillez vous reconnecter.', 401);
            }
        }

        if (error instanceof ApiError) {
            throw error;
        }

        // Handle network errors
        console.error('Network error:', error);
        throw new ApiError('Erreur de connexion au serveur', 0);
    }
};

// Orders API
export const ordersApi = {
    // Get all orders with optional filters
    getAll: async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.prosthetistId) params.append('prosthetistId', filters.prosthetistId);
        if (filters.clientId) params.append('clientId', filters.clientId);
        if (filters.status) params.append('status', filters.status);

        const queryString = params.toString();
        const url = `/orders${queryString ? `?${queryString}` : ''}`;

        const response = await apiRequest(url);
        return response.data;
    },

    // Get order by ID
    getById: async (id) => {
        const response = await apiRequest(`/orders/${id}`);
        return response.data;
    },

    // Create new order
    create: async (orderData) => {
        const response = await apiRequest('/orders', {
            method: 'POST',
            body: JSON.stringify(orderData),
        });
        return response.data;
    },

    // Update order status
    updateStatus: async (id, status) => {
        const response = await apiRequest(`/orders/${id}/status`, {
            method: 'PUT',
            body: JSON.stringify({ status }),
        });
        return response.data;
    },

    // Update order (edit fields)
    update: async (id, orderData) => {
        const response = await apiRequest(`/orders/${id}`, {
            method: 'PUT',
            body: JSON.stringify(orderData),
        });
        return response.data;
    },

    // Delete order
    delete: async (id) => {
        const response = await apiRequest(`/orders/${id}`, {
            method: 'DELETE',
        });
        return response;
    },

    // Add message to order
    addMessage: async (orderId, messageData) => {
        const response = await apiRequest(`/orders/${orderId}/messages`, {
            method: 'POST',
            body: JSON.stringify(messageData),
        });
        return response.data;
    },

    // Notes internes (prothésiste/admin uniquement)
    getNotes: async (orderId) => {
        const response = await apiRequest(`/orders/${orderId}/notes`);
        return response.data;
    },

    addNote: async (orderId, note) => {
        const response = await apiRequest(`/orders/${orderId}/notes`, {
            method: 'POST',
            body: JSON.stringify({ note }),
        });
        return response.data;
    },

    deleteNote: async (orderId, noteId) => {
        await apiRequest(`/orders/${orderId}/notes/${noteId}`, { method: 'DELETE' });
    },

    // Mark messages as read
    markMessagesAsRead: async (orderId, userType) => {
        const response = await apiRequest(`/orders/${orderId}/messages/read`, {
            method: 'PUT',
            body: JSON.stringify({ userType }),
        });
        return response.data;
    },

    // Get unread messages count
    getUnreadMessagesCount: async (orderId, userType) => {
        const response = await apiRequest(`/orders/${orderId}/messages/unread?userType=${userType}`);
        return response.data.count;
    },

    // Search patients for autocomplete
    searchPatients: async (searchTerm, clientId = null) => {
        const params = new URLSearchParams({ q: searchTerm });
        if (clientId) {
            params.append('clientId', clientId);
        }
        const response = await apiRequest(`/orders/patients/search?${params}`);
        return response.data;
    },

    // Get invoice metadata covering an order (null if none)
    getInvoice: async (orderId) => {
        const response = await apiRequest(`/orders/${orderId}/invoice`);
        return response.data;
    },

    // Download the invoice PDF covering an order (authenticated blob)
    downloadInvoiceBlob: async (orderId) => {
        const url = `${API_BASE_URL}/orders/${orderId}/invoice/pdf`;
        const token = localStorage.getItem('accessToken');
        const response = await fetch(url, {
            method: 'GET',
            headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.blob();
    },
};

// Invoices API (facturation groupée)
export const invoicesApi = {
    // Liste des factures existantes (prothésiste/admin)
    list: async () => {
        const response = await apiRequest('/invoices');
        return response.data;
    },

    // Génère une facture groupée. payload: { clientId, groups:[...], notes }
    create: async (payload) => {
        const response = await apiRequest('/invoices', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
        return response.data;
    },

    // Télécharge le PDF d'une facture (blob authentifié)
    downloadBlob: async (invoiceId) => {
        const url = `${API_BASE_URL}/invoices/${invoiceId}/pdf`;
        const token = localStorage.getItem('accessToken');
        const response = await fetch(url, {
            method: 'GET',
            headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.blob();
    },

    // Annule une facture : la supprime et remet ses commandes en expédiée
    remove: async (invoiceId) => {
        return await apiRequest(`/invoices/${invoiceId}`, { method: 'DELETE' });
    },
};

// Authentication API
export const authApi = {
    // Register new user
    register: async (userData) => {
        // Le compte est créé inactif : activation requise par email, pas d'auto-login.
        const response = await apiRequest('/auth/register', {
            method: 'POST',
            body: JSON.stringify(userData),
        });
        return response.data;
    },

    // Activate account with token received by email
    activateAccount: async (token) => {
        const response = await apiRequest('/auth/activate', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });
        return response;
    },

    // Resend the activation email for an unverified account
    resendActivation: async (email) => {
        const response = await apiRequest('/auth/resend-activation', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
        return response;
    },

    // Login user
    login: async (credentials) => {
        const response = await apiRequest('/auth/login', {
            method: 'POST',
            body: JSON.stringify(credentials),
        });
        
        // Sauvegarder le token après connexion réussie
        if (response.success && response.data.accessToken) {
            localStorage.setItem('accessToken', response.data.accessToken);
        }
        
        return response.data;
    },

    // Logout user
    logout: async () => {
        try {
            const response = await apiRequest('/auth/logout', {
                method: 'POST',
            });
            
            // Nettoyer le token même en cas d'erreur serveur
            localStorage.removeItem('accessToken');
            
            return response;
        } catch (error) {
            // Nettoyer le token même en cas d'erreur
            localStorage.removeItem('accessToken');
            throw error;
        }
    },

    // Refresh access token
    refreshToken: async () => {
        try {
            const response = await apiRequest('/auth/refresh', {
                method: 'POST',
            });
            
            if (response.success && response.data.accessToken) {
                localStorage.setItem('accessToken', response.data.accessToken);
                return response.data;
            }
            
            throw new Error('Échec du rafraîchissement du token');
        } catch (error) {
            // Token de refresh invalide, nettoyer le localStorage
            localStorage.removeItem('accessToken');
            throw error;
        }
    },

    // Get current user profile
    getProfile: async () => {
        const response = await apiRequest('/auth/profile');
        return response.data;
    },

    // Update user profile
    updateProfile: async (profileData) => {
        const response = await apiRequest('/auth/profile', {
            method: 'PUT',
            body: JSON.stringify(profileData),
        });
        return response.data;
    },

    // Update UI theme
    updateTheme: async (themeData) => {
        const response = await apiRequest('/auth/theme', {
            method: 'PUT',
            body: JSON.stringify(themeData),
        });
        return response.data;
    },

    // Mark a one-shot notice as seen
    dismissNotice: async (noticeId) => {
        const response = await apiRequest('/auth/notices/dismiss', {
            method: 'POST',
            body: JSON.stringify({ noticeId }),
        });
        return response.data;
    },

    // Change password
    changePassword: async (passwordData) => {
        const response = await apiRequest('/auth/change-password', {
            method: 'PUT',
            body: JSON.stringify(passwordData),
        });
        return response;
    },

    // Get billing address
    getBillingAddress: async () => {
        const response = await apiRequest('/auth/billing-address');
        return response.data;
    },

    // Update billing address
    updateBillingAddress: async (billingData) => {
        const response = await apiRequest('/auth/billing-address', {
            method: 'PUT',
            body: JSON.stringify(billingData),
        });
        return response.data;
    },

    // Request password reset
    forgotPassword: async (email) => {
        const response = await apiRequest('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
        return response;
    },

    // Reset password with token
    resetPassword: async (token, newPassword) => {
        const response = await apiRequest('/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, newPassword }),
        });
        return response;
    },

    // Check if user is authenticated
    isAuthenticated: () => {
        return !!localStorage.getItem('accessToken');
    },

    // Get current token
    getToken: () => {
        return localStorage.getItem('accessToken');
    },

    // Clear authentication data
    clearAuth: () => {
        localStorage.removeItem('accessToken');
    }
};

// Files API
export const filesApi = {
    // Upload files for an order
    upload: async (orderId, files) => {
        const createFormData = () => {
            const formData = new FormData();
            // Add files to FormData
            if (Array.isArray(files)) {
                files.forEach(file => {
                    formData.append('files', file);
                });
            } else {
                formData.append('files', files);
            }
            return formData;
        };

        const response = await apiRequest(`/files/upload/${orderId}`, {
            method: 'POST',
            // Pas de Content-Type : le navigateur le pose avec le boundary multipart.
            body: createFormData(),
            // Pass function to recreate FormData for retries
            _formDataCreator: createFormData,
            _isFileUpload: true
        });
        return response.data;
    },

    // Get file info
    getInfo: async (fileId) => {
        const response = await apiRequest(`/files/${fileId}/info`);
        return response.data;
    },

    // Delete file
    delete: async (fileId) => {
        const response = await apiRequest(`/files/${fileId}`, {
            method: 'DELETE',
        });
        return response;
    },

    // Get order files
    getOrderFiles: async (orderId) => {
        const response = await apiRequest(`/files/order/${orderId}`);
        return response.data;
    },

    // Download file as blob with authentication
    downloadBlob: async (fileId) => {
        const url = `${API_BASE_URL}/files/${fileId}/download`;
        const token = localStorage.getItem('accessToken');
        
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.blob();
    },
};

// Users API
export const usersApi = {
    // Get all prosthetists
    getProsthetists: async () => {
        const response = await apiRequest('/users/prosthetists');
        return response.data;
    },

    // Get all clients
    getClients: async () => {
        const response = await apiRequest('/users/clients');
        return response.data;
    },
};

// Feature Requests API
export const featureRequestsApi = {
    // Create a new feature request
    create: async (requestData) => {
        const response = await apiRequest('/feature-requests', {
            method: 'POST',
            body: JSON.stringify(requestData),
        });
        return response.data;
    },

    // Get user's feature requests
    getMyRequests: async () => {
        const response = await apiRequest('/feature-requests/my-requests');
        return response.data;
    },

    // Get all feature requests (admin only)
    getAll: async (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.priority) params.append('priority', filters.priority);
        
        const queryString = params.toString();
        const url = `/feature-requests${queryString ? `?${queryString}` : ''}`;
        
        const response = await apiRequest(url);
        return response.data;
    },

    // Update feature request (admin only)
    update: async (id, updateData) => {
        const response = await apiRequest(`/feature-requests/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updateData),
        });
        return response.data;
    },

    // Delete feature request (admin only)
    delete: async (id) => {
        const response = await apiRequest(`/feature-requests/${id}`, {
            method: 'DELETE',
        });
        return response;
    },

    // Get feature request statistics (admin only)
    getStats: async () => {
        const response = await apiRequest('/feature-requests/stats');
        return response.data;
    },
};

// Health check
export const healthCheck = async () => {
    try {
        const response = await apiRequest('/health');
        return response;
    } catch (error) {
        console.error('Health check failed:', error);
        throw error;
    }
};

// Export API base URL for WebSocket client
export const getApiBaseUrl = () => API_BASE_URL;

const apiExports = {
    authApi,
    ordersApi,
    filesApi,
    usersApi,
    featureRequestsApi,
    healthCheck,
    ApiError,
    getApiBaseUrl,
};

export default apiExports;